import type { ReactNode } from "react";
import { Card, CardContent } from "@/ui/base/card";
import { Label } from "@/ui/base/label";
import { Input } from "@/ui/base/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/base/select";
import { Search } from "lucide-react";
import { ClearFiltersButton } from "@/ui/ClearFiltersButton";

export interface FilterSelectOption {
  value: string;
  label: string;
}

export interface FilterFieldConfig {
  /** Unique key for the filter */
  key: string;
  /** Label displayed above the field */
  label: string;
  /** Type of filter input */
  type: "search" | "select" | "date" | "month";
  /** Placeholder text */
  placeholder?: string;
  /** For select type: list of options */
  options?: FilterSelectOption[];
  /** Whether to include a "Todos" option for selects (default: true) */
  showAllOption?: boolean;
  /** Custom label for the "all" option (default: "Todos") */
  allOptionLabel?: string;
  /** Width class for the field (default: auto) */
  width?: string;
  /** Whether this field should grow to fill available space (default: false, true for search) */
  flex?: boolean;
  /** aria-label for accessibility */
  ariaLabel?: string;
}

interface FilterBarProps {
  /** Filter field configurations */
  fields: FilterFieldConfig[];
  /** Current filter values keyed by field key */
  values: Record<string, string>;
  /** Called when a filter value changes */
  onChange: (key: string, value: string) => void;
  /** Called when all filters should be cleared */
  onClear: () => void;
  /**
   * Whether any filter is currently active. Override opcional — quando omitido,
   * deriva automaticamente comparando `values` com `defaultValues` (ou string vazia).
   */
  hasActiveFilters?: boolean;
  /** Defaults declarados pela tela (ex.: range padrão, status default). */
  defaultValues?: Record<string, string>;
  /** Optional extra content to render after filters (e.g. custom buttons) */
  children?: ReactNode;
}

export function FilterBar({ fields, values, onChange, onClear, hasActiveFilters, defaultValues, children }: FilterBarProps) {
  // Cálculo padrão: ativo se difere do default declarado.
  const derivedActiveCount = Object.keys(values).reduce((acc, k) => {
    const v = values[k] ?? "";
    const d = defaultValues?.[k] ?? "";
    if (v === d) return acc;
    if (!v && !d) return acc;
    return acc + 1;
  }, 0);
  const showClear = hasActiveFilters ?? derivedActiveCount > 0;
  const activeCount = hasActiveFilters !== undefined
    ? (hasActiveFilters ? Math.max(derivedActiveCount, 1) : 0)
    : derivedActiveCount;

  // Global rule: status filter always renders last (after any extra children like custom multi-selects)
  // Matches "status" exactly OR keys ending in "_status" / "status" (e.g. p_status, r_status, f_status)
  const isStatusKey = (key: string) => {
    const k = key.toLowerCase();
    return k === "status" || k.endsWith("_status") || k.endsWith("status");
  };
  const statusFields = fields.filter((f) => isStatusKey(f.key));
  const nonStatusFields = fields.filter((f) => !isStatusKey(f.key));

  const renderField = (field: FilterFieldConfig) => {
    const fieldValue = values[field.key] ?? "";

    if (field.type === "search") {
      return (
        <div key={field.key} className={field.flex !== false ? "flex-1 space-y-1" : "space-y-1"}>
          <Label>{field.label}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={field.placeholder ?? "Buscar..."}
              value={fieldValue}
              onChange={(e) => onChange(field.key, e.target.value)}
              className="pl-9"
              aria-label={field.ariaLabel ?? field.placeholder ?? field.label}
            />
          </div>
        </div>
      );
    }

    if (field.type === "select") {
      const showAll = field.showAllOption !== false;
      const selectValue = fieldValue || (showAll ? "ALL" : "");

      return (
        <div key={field.key} className="space-y-1">
          <Label>{field.label}</Label>
          <Select
            value={selectValue}
            onValueChange={(v) => onChange(field.key, v === "ALL" ? "" : v)}
          >
            <SelectTrigger
              className={field.width ?? "w-full sm:w-[160px]"}
              aria-label={field.ariaLabel ?? field.label}
            >
              <SelectValue placeholder={field.placeholder ?? field.label} />
            </SelectTrigger>
            <SelectContent>
              {showAll && <SelectItem value="ALL">{field.allOptionLabel ?? "Todos"}</SelectItem>}
              {field.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === "date" || field.type === "month") {
      return (
        <div key={field.key} className="space-y-1">
          <Label>{field.label}</Label>
          <Input
            type={field.type}
            value={fieldValue}
            onChange={(e) => onChange(field.key, e.target.value)}
            className={field.width ?? "w-full sm:w-[180px]"}
            aria-label={field.ariaLabel ?? field.label}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-end">
          {nonStatusFields.map(renderField)}
          {children}
          {statusFields.map(renderField)}

          <ClearFiltersButton
            active={showClear}
            count={activeCount}
            onClear={onClear}
            className="self-end"
          />
        </div>
      </CardContent>
    </Card>
  );
}
